import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  Divider,
  Card,
  CardContent,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Collapse,
  Button,
  useMediaQuery,
} from '@mui/material';
import ViewerDialogContent from './ViewerDialogContent';
import ViewerDialogFrame from './ViewerDialogFrame';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Subject as SubjectIcon,
  Attachment as AttachmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewerData } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '85vw',
    height: '85vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const EmailContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f8f9fa',
  overflow: 'hidden',
}));

const EmailHeader = styled(Card)(({ theme }) => ({
  margin: '8px 16px 0 16px',
  backgroundColor: '#fafafa',
  borderRadius: '6px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(87, 87, 176, 0.08)',
  [theme.breakpoints.down('sm')]: {
    margin: '8px 12px 0 12px',
  },
}));

const EmailBody = styled(Box)(({ theme }) => ({
  flex: 1,
  margin: '8px 16px 16px 16px',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    margin: '8px 12px 12px 12px',
  },
}));

const EmailContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '20px',
  overflow: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
  '& iframe': {
    width: '100%',
    minHeight: '400px',
    border: 'none',
  },
  '& pre': {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'inherit',
    fontSize: '14px',
    lineHeight: '1.5',
    margin: 0,
  },
}));

const AttachmentsList = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderTop: '1px solid rgba(87, 87, 176, 0.1)',
  backgroundColor: '#f8f9fa',
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '3px 0',
  fontSize: '0.85rem',
  '& .MuiChip-root': {
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  '& .MuiTypography-body2': {
    fontSize: '0.8rem',
  },
}));

const EmailViewer = ({ open, onClose, filename = '', eventId = null, filePath = '', embedded = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data: emailData,
    loading,
    error,
  } = useViewerData({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.email_missing_source', 'File id or path is required'),
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [blobUrl, setBlobUrl] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setAttachmentsExpanded(false);
    }
  }, [open, eventId, filename, filePath]);

  useEffect(() => {
    if (!open || !emailData?.html_body?.trim()) {
      setHtmlContent('');
      setBlobUrl(null);
      return undefined;
    }

    const content = emailData.html_body;
    const url = URL.createObjectURL(new Blob([content], { type: 'text/html;charset=utf-8' }));
    setHtmlContent(content);
    setBlobUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [open, emailData]);

  const downloadEmail = () => {
    if (emailData) {
      // Create a formatted email export
      const emailContent = `
From: ${emailData.from || 'Unknown'}
To: ${emailData.to || 'Unknown'}
Subject: ${emailData.subject || 'No Subject'}
Date: ${emailData.date || 'Unknown'}
${emailData.cc ? `CC: ${emailData.cc}` : ''}
${emailData.bcc ? `BCC: ${emailData.bcc}` : ''}

${emailData.text_body || emailData.html_body || 'No content'}

${emailData.attachments && emailData.attachments.length > 0 ? `
Attachments:
${emailData.attachments.map(att => `- ${att.filename} (${att.size || 'unknown size'})`).join('\n')}
` : ''}
      `.trim();
      
      const blob = new Blob([emailContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename || 'email'}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <ViewerDialogFrame
      embedded={embedded}
      DialogComponent={StyledDialog}
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <EmailIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.email_viewer_title')} - ${filename}` : t('viewer.email_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadEmail} 
            disabled={!emailData}
            size="small"
            title={t('viewer.download_email')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
        
        {emailData && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              icon={<PersonIcon />}
              label={emailData.from || 'Unknown Sender'}
              size="small"
              variant="outlined"
            />
            <Chip 
              icon={<AccessTimeIcon />}
              label={formatDate(emailData.date)}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </ToolbarContainer>

      <ViewerDialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <EmailContainer ref={containerRef}>
          {(() => {
            if (loading) {
              return (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  gap: 2
                }}>
                  <LinearProgress sx={{ width: 300 }} />
                  <Typography color="text.secondary">
                    {t('viewer.loading_email')}
                  </Typography>
                </Box>
              );
            }
            
            if (error) {
              return (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  padding: 4
                }}>
                  <Typography color="error" variant="h6" gutterBottom>
                    {t('viewer.error_loading_email')}
                  </Typography>
                  <Typography color="text.secondary" sx={{ textAlign: 'center', whiteSpace: 'pre-line', mb: 2 }}>
                    {error}
                  </Typography>
                </Box>
              );
            }
            
            if (emailData) {
              return (
                <>
                  {/* Email Header */}
                  <EmailHeader>
                    <CardContent sx={{ padding: '12px 16px !important' }}>
                      <HeaderRow>
                        <SubjectIcon color="primary" sx={{ fontSize: '1rem' }} />
                        <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                          {emailData.subject || 'No Subject'}
                        </Typography>
                      </HeaderRow>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <HeaderRow>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50, fontSize: '0.8rem', fontWeight: 500 }}>
                          From:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {emailData.from || 'Unknown'}
                        </Typography>
                      </HeaderRow>
                      
                      <HeaderRow>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50, fontSize: '0.8rem', fontWeight: 500 }}>
                          To:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {emailData.to || 'Unknown'}
                        </Typography>
                      </HeaderRow>
                      
                      {emailData.cc && (
                        <HeaderRow>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50, fontSize: '0.8rem', fontWeight: 500 }}>
                            CC:
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {emailData.cc}
                          </Typography>
                        </HeaderRow>
                      )}
                      
                      {emailData.bcc && (
                        <HeaderRow>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50, fontSize: '0.8rem', fontWeight: 500 }}>
                            BCC:
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {emailData.bcc}
                          </Typography>
                        </HeaderRow>
                      )}
                      
                      <HeaderRow>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50, fontSize: '0.8rem', fontWeight: 500 }}>
                          Date:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {formatDate(emailData.date)}
                        </Typography>
                      </HeaderRow>
                      
                      {emailData.priority && (
                        <HeaderRow>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50, fontSize: '0.8rem', fontWeight: 500 }}>
                            Priority:
                          </Typography>
                          <Chip 
                            label={emailData.priority}
                            size="small"
                            color={emailData.priority === 'High' ? 'error' : 'default'}
                            sx={{ height: '20px', fontSize: '0.7rem' }}
                          />
                        </HeaderRow>
                      )}
                    </CardContent>
                  </EmailHeader>

                  {/* Email Body */}
                  <EmailBody>
                    <EmailContent>
                      {htmlContent && blobUrl ? (
                        <iframe
                          src={blobUrl}
                          title="Email Content"
                          sandbox="allow-same-origin"
                          style={{ width: '100%', minHeight: '400px', border: 'none' }}
                        />
                      ) : emailData.text_body ? (
                        <pre>{emailData.text_body}</pre>
                      ) : (
                        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No content available
                        </Typography>
                      )}
                    </EmailContent>
                    
                    {/* Attachments */}
                    {emailData.attachments && emailData.attachments.length > 0 && (
                      <AttachmentsList>
                        <Button
                          onClick={() => setAttachmentsExpanded(!attachmentsExpanded)}
                          startIcon={<AttachmentIcon />}
                          endIcon={attachmentsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          sx={{ mb: 1 }}
                        >
                          {emailData.attachments.length} Attachment{emailData.attachments.length !== 1 ? 's' : ''}
                        </Button>
                        
                        <Collapse in={attachmentsExpanded}>
                          <List dense>
                            {emailData.attachments.map((attachment, index) => (
                              <ListItem key={index}>
                                <ListItemAvatar>
                                  <Avatar sx={{ bgcolor: primaryColor }}>
                                    <AttachmentIcon />
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={attachment.filename || `Attachment ${index + 1}`}
                                  secondary={`${attachment.content_type || 'Unknown type'} â€¢ ${formatSize(attachment.size)}`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </AttachmentsList>
                    )}
                  </EmailBody>
                </>
              );
            }
            
            return (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                padding: 4
              }}>
                <Typography color="text.secondary">
                  {t('viewer.no_email_content')}
                </Typography>
              </Box>
            );
          })()}
        </EmailContainer>
      </ViewerDialogContent>
    </ViewerDialogFrame>
  );
};

export default EmailViewer;
